import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { BrandMark } from '../components/brand.tsx';
import { Button } from '../components/ui/button.tsx';
import { authConfig, beginSignIn, tokenStore } from '../lib/auth.ts';

const INTRO = [
  [350, 's-mark'],
  [1150, 's-word'],
  [2900, 's-exit'],
  [3900, 's-short'],
  [5200, 's-split'],
  [5800, 's-settle'],
] as const;
const INTRO_SEEN_KEY = 'react-intro-seen';

type Letter = [char: string, role: 'keep' | 'drop' | 'swap' | 'in', coral?: true];
// "Revenue ACceleraTor" -> "ReAct": `drop` letters leave one by one, then the gaps close while the
// capitals C and T (`swap`) give way to c and t (`in`).
const WORD: Letter[] = [
  ['R', 'keep'],
  ['e', 'keep'],
  ...[...'venue '].map((c): Letter => [c, 'drop']),
  ['A', 'keep', true],
  ['C', 'swap', true],
  ['c', 'in', true],
  ...[...'celera'].map((c): Letter => [c, 'drop', true]),
  ['T', 'swap', true],
  ['t', 'in', true],
  ['o', 'drop', true],
  ['r', 'drop', true],
];

function introSeen() {
  try {
    return localStorage.getItem(INTRO_SEEN_KEY) !== null;
  } catch (err) {
    console.warn('sign-in: localStorage unavailable, playing the intro', err);
    return false;
  }
}

// Written when the intro ends, not when it starts: Strict Mode's second effect run would read it
// and skip the intro, and a visitor who leaves mid-intro sees it again.
function markIntroSeen() {
  try {
    localStorage.setItem(INTRO_SEEN_KEY, '1');
  } catch (err) {
    console.warn('sign-in: localStorage unavailable, the intro will play again', err);
  }
}

/**
 * The only public page, laid out as Relay's login: an intro (mark, "Revenue ACceleraTor" folding
 * into "ReAct") that splits
 * into the brand pane and the sign-in pane. The intro plays once per browser and never with
 * reduced motion. With Cognito configured the button hands the browser to the hosted UI (Google
 * only — see infra/modules/cognito); without it, the page says what to configure. In a dev build
 * with the mock API, a mock session is one click away.
 */
export function SignInPage() {
  const search = useSearch({ from: '/sign-in' });
  const navigate = useNavigate();
  const cfg = authConfig();
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('');
  const [wavesLive, setWavesLive] = useState(false);
  const mock = import.meta.env.DEV && import.meta.env.VITE_API_MOCK === 'true';

  useEffect(() => {
    if (tokenStore.get()) void navigate({ to: search.returnTo ?? '/', replace: true });
  }, [navigate, search.returnTo]);

  const runIntro = useCallback(() => {
    setStage('');
    const timers = INTRO.map(([ms, cls]) =>
      setTimeout(() => {
        setStage((p) => (p ? `${p} ${cls}` : cls));
        if (cls === 's-settle') markIntroSeen();
      }, ms),
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (introSeen() || reduce) {
      setStage(`instant ${INTRO.map(([, cls]) => cls).join(' ')}`);
      return;
    }
    return runIntro();
  }, [runIntro]);

  useEffect(() => {
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => setWavesLive(true), { timeout: 1000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(() => setWavesLive(true), 200);
    return () => window.clearTimeout(id);
  }, []);

  const start = async () => {
    if (!cfg) return;
    setBusy(true);
    await beginSignIn(cfg, { returnTo: search.returnTo ?? '/' });
  };
  const useMock = () => {
    tokenStore.set({ idToken: 'mock.session.token', expiresAt: Date.now() + 12 * 60 * 60_000 });
    void navigate({ to: search.returnTo ?? '/', replace: true });
  };

  return (
    <div className={`signin ${stage}${wavesLive ? ' waves-live' : ''}`}>
      {/* `dark` scopes the brand tokens, so the mark draws light on the navy pane. */}
      <section className="signin-brand dark">
        <div className="signin-waves" aria-hidden>
          <span className="signin-wave w1" />
          <span className="signin-wave w2" />
          <span className="signin-wave w3" />
          <span className="signin-wave w4" />
        </div>
        <div className="signin-block">
          <BrandMark className="signin-mark" aria-hidden />
          <div className="signin-word" role="img" aria-label="ReAct">
            {WORD.map(([char, role, coral], i) => (
              <span
                key={i}
                className={`signin-lt signin-lt-${role}${coral ? ' signin-lt-coral' : ''}`}
                style={{ '--i': i } as CSSProperties}
              >
                <span>{char}</span>
              </span>
            ))}
          </div>
          <p className="signin-tag">Revenue Accelerator by Remote Leverage</p>
        </div>
        <button type="button" className="signin-replay" onClick={runIntro}>
          &#8635; Replay intro
        </button>
      </section>

      <main className="signin-auth">
        <div className="signin-inner text-center">
          <h1 className="text-foreground text-2xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-muted-foreground mt-1.5 mb-8 text-sm">Sign in to ReAct</p>
          {search.error && (
            <p
              className="bg-state-danger-bg text-state-danger-fg border-state-danger-border mb-4 rounded-lg border px-3 py-2 text-left text-sm"
              role="alert"
            >
              {search.error}
            </p>
          )}
          {cfg ? (
            <button
              type="button"
              onClick={() => void start()}
              disabled={busy}
              data-testid="sign-in"
              className="border-border-hover bg-surface text-foreground inline-flex w-full items-center justify-center gap-3 rounded-full border px-5 py-3.5 text-[15px] font-semibold shadow-sm transition-all hover:-translate-y-px hover:shadow-md disabled:opacity-60"
            >
              {busy ? (
                <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <GoogleIcon />
              )}
              {/* Its own element: Google Translate swaps bare text nodes, and React's swap of the
                  icon beside one then crashes the page (Relay's RELAY-6Y). */}
              <span>{busy ? 'Signing in…' : 'Continue with Google'}</span>
            </button>
          ) : (
            <p className="text-muted-foreground text-left text-sm" data-testid="not-configured">
              Sign-in is not configured for this build: set <code>VITE_COGNITO_DOMAIN</code> and{' '}
              <code>VITE_COGNITO_CLIENT_ID</code> (the Terraform <code>hosted_ui_domain</code> and{' '}
              <code>web_client_id</code> outputs).
            </p>
          )}
          <p className="text-muted-foreground mt-5 text-xs">
            Only <b className="text-foreground/80">@remoteleverage.com</b> accounts have access.
          </p>
          {mock && (
            <Button
              variant="outline"
              className="mt-6 w-full"
              onClick={useMock}
              data-testid="mock-sign-in"
            >
              Use the mock session (dev)
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="size-[18px]" viewBox="0 0 48 48" aria-hidden>
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}
